// SPDX-License-Identifier: GPL-3.0-or-later
// Haley, the ghost SigmaBot: Ghost Ramp host (THEME.md section 5.3).
// She is a round, see-through bot. She shows ghost targets that are not real. Only solid targets score.
// A solid target has a red outline, so the player can always see which target is real.

import {
  BackSide,
  BoxGeometry,
  type Material,
  type Mesh,
  type MeshBasicMaterial,
  type Object3D,
} from "three";
import { BRAND_HEX } from "../art/canvas.ts";
import { drawTextPanel } from "../art/labels.ts";
import { addBot } from "./bot.ts";
import {
  type Built,
  type CharacterEnv,
  disposeTree,
  makeLabel,
  motion,
  namedGroup,
  OUTLINE_KEY,
  put,
  sec,
  solid,
  wire,
} from "./common.ts";
import type { SigmaBotSpec } from "./sigmabot/index.ts";

/** The look of Haley. */
export const HALEY_SPEC: SigmaBotSpec = {
  body: "round",
  palette: "snow",
  eyes: "dizzy",
  mouth: "oh",
  hat: "none",
  accessory: "monocle",
};

/** Size of one body unit of Haley, in recipe units. */
const SCALE = 1.05;

/** Time of the pulse after a shot. */
export const HALEY_PULSE_MS = 300;
/** Ghost flicker rate in Hz. It stays below 3 Hz. */
export const GHOST_FLICKER_HZ = 1.5;
/** Opacity of the ghost bot at rest and in the pulse. */
const BOT_OPACITY = 0.6;
const PULSE_OPACITY = 1;

/** Every solid material of the bot (not the outline hull, which is a basic material). */
function botMaterials(bot: Object3D): Material[] {
  const out: Material[] = [];
  bot.traverse((o) => {
    const m = (o as Mesh).material as Material | undefined;
    if (m && "roughness" in m) out.push(m);
  });
  return out;
}

/**
 * A see-through part has no outline (THEME.md section 5). Remove the outline hulls of the bot:
 * a white shell behind a see-through body would show through it.
 */
function removeOutlines(bot: Object3D): void {
  const hulls: Object3D[] = [];
  bot.traverse((o) => {
    if (o.userData[OUTLINE_KEY] === true) hulls.push(o);
  });
  for (const hull of hulls) {
    hull.removeFromParent();
    ((hull as Mesh).material as Material).dispose();
  }
}

export function buildHaley(env: CharacterEnv): Built {
  const group = namedGroup("Haley");

  const bot = addBot(group, "HaleyBot", HALEY_SPEC, SCALE);
  removeOutlines(bot);
  const ghostMaterials = botMaterials(bot);
  for (const m of ghostMaterials) {
    m.transparent = true;
    m.opacity = BOT_OPACITY;
  }
  // A "?" over her head: she does not know what is real.
  const face = makeLabel(env, 20, 20, 128, 128, (ctx, w, h) => drawTextPanel(ctx, w, h, ["?"]));
  face.mesh.position.set(0, 96, 0);
  group.add(face.mesh);

  // Ghost targets (wireframe, display only) and one real target (solid, with a red outline).
  const ghosts: Mesh<BoxGeometry, MeshBasicMaterial>[] = [];
  const ghostX = [-60, -20, 60];
  for (const x of ghostX) {
    const ghost = put(new BoxGeometry(30, 40, 8), wire(BRAND_HEX.edge), x, 150, 0);
    ghost.name = `HaleyGhost${ghosts.length}`;
    ghosts.push(ghost);
    group.add(ghost);
  }
  group.add(put(new BoxGeometry(30, 40, 8), solid(BRAND_HEX.gray888), 20, 150, 0));
  const ring = put(
    new BoxGeometry(30, 40, 8),
    solid(BRAND_HEX.mark, { side: BackSide }),
    20,
    150,
    0,
  );
  ring.scale.setScalar(1.08);
  group.add(ring);

  let pulseStart = Number.NEGATIVE_INFINITY;
  return {
    group,
    tick(timeMs) {
      const a = motion(env);
      const t = sec(timeMs);
      bot.position.y = 2 * Math.sin(t * 2) * a;
      // Ghosts flicker. With reduced motion they show at full opacity and do not flicker.
      const flicker = 1 - a * 0.3 * (0.5 + 0.5 * Math.sin(2 * Math.PI * GHOST_FLICKER_HZ * t));
      for (const ghost of ghosts) {
        ghost.material.transparent = true;
        ghost.material.opacity = flicker;
      }
      const elapsed = timeMs - pulseStart;
      const pulse = a > 0 && elapsed >= 0 && elapsed < HALEY_PULSE_MS;
      for (const m of ghostMaterials) m.opacity = pulse ? PULSE_OPACITY : BOT_OPACITY;
    },
    play(name, _arg, timeMs) {
      if (name === "shot") pulseStart = timeMs;
    },
    dispose() {
      disposeTree(group);
    },
  };
}
